import { useEffect } from "react";

/**
 * Small hook to update <meta>, <title> and <link rel="canonical"> tags in-place
 * on route change. Unlike react-helmet-async which appends new tags, this hook
 * REPLACES the value of existing tags — critical for social crawlers that pick
 * the first match. On unmount it restores the previous values so navigating
 * back to non-article pages doesn't leave stale article metadata.
 *
 * @param {object} meta
 * @param {string} [meta.title]
 * @param {string} [meta.description]
 * @param {string} [meta.canonical]
 * @param {object} [meta.og]       // { title, description, url, image, type }
 * @param {object} [meta.twitter]  // { title, description, image, card }
 */
export default function usePageMeta({ title, description, canonical, og = {}, twitter = {} } = {}) {
  useEffect(() => {
    const restorers = [];

    // <title>
    if (title != null) {
      const original = document.title;
      document.title = title;
      restorers.push(() => { document.title = original; });
    }

    // Helper — set attribute on the FIRST matching meta tag (or create one if absent)
    const setMeta = (selector, attr, content) => {
      let el = document.head.querySelector(selector);
      let created = false;
      if (!el) {
        el = document.createElement("meta");
        // Selector shape: meta[name="x"] or meta[property="x"]
        const m = selector.match(/meta\[(name|property)="([^"]+)"\]/);
        if (m) el.setAttribute(m[1], m[2]);
        document.head.appendChild(el);
        created = true;
      }
      const original = el.getAttribute(attr);
      el.setAttribute(attr, content);
      restorers.push(() => {
        if (created) el.remove();
        else if (original == null) el.removeAttribute(attr);
        else el.setAttribute(attr, original);
      });
    };

    // <link rel="canonical">
    const setLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      let created = false;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
        created = true;
      }
      const original = el.getAttribute("href");
      el.setAttribute("href", href);
      restorers.push(() => {
        if (created) el.remove();
        else if (original == null) el.removeAttribute("href");
        else el.setAttribute("href", original);
      });
    };

    if (description != null) setMeta('meta[name="description"]', "content", description);
    if (canonical != null) setLink("canonical", canonical);

    // Open Graph
    if (og.title != null)       setMeta('meta[property="og:title"]',       "content", og.title);
    if (og.description != null) setMeta('meta[property="og:description"]', "content", og.description);
    if (og.url != null)         setMeta('meta[property="og:url"]',         "content", og.url);
    if (og.image != null)       setMeta('meta[property="og:image"]',       "content", og.image);
    if (og.type != null)        setMeta('meta[property="og:type"]',        "content", og.type);

    // Twitter Card
    if (twitter.title != null)       setMeta('meta[name="twitter:title"]',       "content", twitter.title);
    if (twitter.description != null) setMeta('meta[name="twitter:description"]', "content", twitter.description);
    if (twitter.image != null)       setMeta('meta[name="twitter:image"]',       "content", twitter.image);
    if (twitter.card != null)        setMeta('meta[name="twitter:card"]',        "content", twitter.card);

    return () => {
      // Restore in reverse order to preserve nested changes
      for (let i = restorers.length - 1; i >= 0; i--) restorers[i]();
    };
  }, [
    title, description, canonical,
    og.title, og.description, og.url, og.image, og.type,
    twitter.title, twitter.description, twitter.image, twitter.card,
  ]);
}
